/**
 * useOrbDocTutorialsAdvancedAiGenerationARDocHeaderLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsAdvancedAiGenerationARDocHeader
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsAdvancedAiGenerationARDocView as OrbDocTutorialsAdvancedAiGenerationARDocViewEntity, OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsAdvancedAiGenerationARDocHeaderState =
  | 'idle';

export type OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent =
  | 'INIT'
  | 'MENU_TOGGLE'
  | 'NAV_LINK'
  | 'NAV_CLICKED';

type OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot = 'main';
interface OrbDocTutorialsAdvancedAiGenerationARDocHeaderInternalState {
  machineState: OrbDocTutorialsAdvancedAiGenerationARDocHeaderState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot, OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsAdvancedAiGenerationARDocViewEntity>;
}

type OrbDocTutorialsAdvancedAiGenerationARDocHeaderAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[]; renderClear?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsAdvancedAiGenerationARDocViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsAdvancedAiGenerationARDocHeaderReducer(
  state: OrbDocTutorialsAdvancedAiGenerationARDocHeaderInternalState,
  action: OrbDocTutorialsAdvancedAiGenerationARDocHeaderAction
): OrbDocTutorialsAdvancedAiGenerationARDocHeaderInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsAdvancedAiGenerationARDocHeaderState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsAdvancedAiGenerationARDocViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsAdvancedAiGenerationARDocViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsAdvancedAiGenerationARDocViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsAdvancedAiGenerationARDocHeaderInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { SiteNavLink } from '@app/shared';
export interface OrbDocTutorialsAdvancedAiGenerationARDocHeaderConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  actionHref?: string;
  actionLabel?: string;
  brandName: string;
  currentLanguage: string;
  currentPath?: string;
  headerContent: { children: Array<{ actions: Array<string | { action: string; actionPayload: { href: string; label: string }; className: Array<string | string[]>; href: string; label: string; size: string; type: string; variant: string } | { className: string[]; size: string; type: string }>; brandHref: string; brandName: string; className: string; logoSrc: string; navigationItems: Array<Array<string | { active: Array<string | string[]>; children: Array<Array<FieldValue[] | string> | Array<string | { active: Array<string | string[]>; href: string[]; label: string[] }> | string>; href: string[]; label: string[] }> | string>; showMenuToggle: boolean; sticky: boolean; type: string; variant: string } | { actions: Array<string | { className: string[]; size: string; type: string }>; brandHref: string; brandName: string; className: string; isMenuOpen: string; logoSrc: string; onMenuToggle: string; showMenuToggle: boolean; sticky: boolean; type: string; variant: string } | { align: string; children: Array<{ action: string; actionPayload: { href: string; label: string }; className: Array<string | string[]>; href: string; label: string; size: string; type: string; variant: string } | { align: string; children: Array<Array<string | string[] | { align: string; children: Array<{ action: string; actionPayload: { href: string[]; label: string[] }; className: string; href: string[]; label: string[]; type: string; variant: Array<Array<string | string[]> | string> } | { align: string; children: Array<Array<FieldValue[] | string> | Array<string | string[] | { action: string; actionPayload: { href: string[]; label: string[] }; className: string; href: string[]; label: string[]; type: string; variant: Array<Array<string | string[]> | string> }> | string>; className: string; direction: string; gap: string; type: string }>; direction: string; gap: string; type: string }> | string>; direction: string; gap: string; type: string }>; className: string[]; direction: string; gap: string; type: string }>; className: string; direction: string; gap: string; type: string };
  homeHref?: string;
  languages: { code: string; href: string; label: string }[];
  links: Array<{ href: string; label: string } | { href: string[]; label: string[] }>;
  logoSrc: string;
  showThemeToggle: boolean;
}

const DEFAULT_ORB_DOC_TUTORIALS_ADVANCED_AI_GENERATION_AR_DOC_HEADER_CONFIG = {
  brandName: "Orb",
  currentLanguage: "@locale",
  headerContent: { children: [{ actions: ["@trait.OrbDocTutorialsAdvancedAiGenerationARSiteHeaderLanguages", { className: ["if", "@config.showThemeToggle", "", "hidden"], size: "sm", type: "theme-toggle" }, { action: "NAV_LINK", actionPayload: { href: "@config.actionHref", label: "@config.actionLabel" }, className: ["if", ["==", "@config.actionLabel", ""], "hidden", ""], href: "@config.actionHref", label: "@config.actionLabel", size: "sm", type: "button", variant: "primary" }], brandHref: "@config.homeHref", brandName: "@config.brandName", className: "hidden lg:flex", logoSrc: "@config.logoSrc", navigationItems: ["array/map", "@config.links", ["fn", "entry", { active: ["==", ["object/get", "@entry", "href", ""], "@config.currentPath"], children: ["array/map", ["object/get", "@entry", "children", []], ["fn", "child", { active: ["==", ["object/get", "@child", "href", ""], "@config.currentPath"], href: ["object/get", "@child", "href", ""], label: ["object/get", "@child", "label", ""] }]], href: ["object/get", "@entry", "href", ""], label: ["object/get", "@entry", "label", ""] }]], showMenuToggle: false, sticky: false, type: "header", variant: "desktop" }, { actions: ["@trait.OrbDocTutorialsAdvancedAiGenerationARSiteHeaderLanguages", { className: ["if", "@config.showThemeToggle", "", "hidden"], size: "sm", type: "theme-toggle" }], brandHref: "@config.homeHref", brandName: "@config.brandName", className: "lg:hidden", isMenuOpen: "@entity.menuOpen", logoSrc: "@config.logoSrc", onMenuToggle: "MENU_TOGGLE", showMenuToggle: true, sticky: false, type: "header", variant: "mobile" }, { align: "stretch", children: [{ align: "stretch", children: ["array/map", "@config.links", ["fn", ["entry"], { align: "stretch", children: [{ action: "NAV_LINK", actionPayload: { href: ["object/get", "@entry", "href", ""], label: ["object/get", "@entry", "label", ""] }, className: "justify-start", href: ["object/get", "@entry", "href", ""], label: ["object/get", "@entry", "label", ""], type: "button", variant: ["if", ["==", ["object/get", "@entry", "href", ""], "@config.currentPath"], "secondary", "ghost"] }, { align: "stretch", children: ["array/map", ["object/get", "@entry", "children", []], ["fn", ["child"], { action: "NAV_LINK", actionPayload: { href: ["object/get", "@child", "href", ""], label: ["object/get", "@child", "label", ""] }, className: "justify-start", href: ["object/get", "@child", "href", ""], label: ["object/get", "@child", "label", ""], type: "button", variant: ["if", ["==", ["object/get", "@child", "href", ""], "@config.currentPath"], "secondary", "ghost"] }]], className: "ps-6", direction: "vertical", gap: "xs", type: "stack" }], direction: "vertical", gap: "xs", type: "stack" }]], direction: "vertical", gap: "xs", type: "stack" }, { action: "NAV_LINK", actionPayload: { href: "@config.actionHref", label: "@config.actionLabel" }, className: ["if", ["==", "@config.actionLabel", ""], "hidden", "mt-2 w-full"], href: "@config.actionHref", label: "@config.actionLabel", size: "md", type: "button", variant: "primary" }], className: ["if", "@entity.menuOpen", "lg:hidden border-b border-border bg-card px-4 py-3", "hidden"], direction: "vertical", gap: "xs", type: "stack" }], className: "sticky top-0 z-50", direction: "vertical", gap: "none", type: "stack" },
  languages: [{ code: "en", href: "/docs/tutorials/advanced/ai-generation", label: "English" }, { code: "ar", href: "/ar/docs/tutorials/advanced/ai-generation", label: "العربية" }, { code: "sl", href: "/sl/docs/tutorials/advanced/ai-generation", label: "Slovenščina" }],
  links: [{ href: ["str/concat", "/ar", "/docs"], label: ["i18n/t", "std-orb-chrome:nav.docs"] }, { href: ["str/concat", "/ar", "/stdlib"], label: ["i18n/t", "std-orb-chrome:nav.stdlib"] }, { href: ["str/concat", "/ar", "/playground"], label: ["i18n/t", "std-orb-chrome:nav.playground"] }, { href: ["str/concat", "/ar", "/blog"], label: ["i18n/t", "std-orb-chrome:nav.blog"] }, { href: "https://github.com/almadar-io/orb", label: "GitHub" }],
  logoSrc: "data:image/svg+xml;utf8,%3Csvg width=%2264%22 height=%2264%22 viewBox=%220 0 100 100%22 fill=%22none%22 xmlns=%22http://www.w3.org/2000/svg%22%3E %3Cdefs%3E %3Cg id=%22slobe%22%3E %3Cellipse cx=%2250%22 cy=%2228%22 rx=%2212%22 ry=%2220%22 fill=%22none%22 stroke=%22%2300b1b8%22 stroke-width=%221.5%22 /%3E %3Cellipse cx=%2250%22 cy=%2272%22 rx=%2212%22 ry=%2220%22 fill=%22none%22 stroke=%22%2300b1b8%22 stroke-width=%221.5%22 /%3E %3C/g%3E %3Cg id=%22slobeInner%22%3E %3Cellipse cx=%2250%22 cy=%2232%22 rx=%227%22 ry=%2214%22 fill=%22none%22 stroke=%22%2300b1b8%22 stroke-width=%221%22 opacity=%220.4%22 /%3E %3Cellipse cx=%2250%22 cy=%2268%22 rx=%227%22 ry=%2214%22 fill=%22none%22 stroke=%22%2300b1b8%22 stroke-width=%221%22 opacity=%220.4%22 /%3E %3C/g%3E %3C/defs%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%280 50 50%29%22 /%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%2830 50 50%29%22 /%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%2860 50 50%29%22 /%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%2890 50 50%29%22 /%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%28120 50 50%29%22 /%3E %3Cuse href=%22%23slobe%22 transform=%22rotate%28150 50 50%29%22 /%3E %3Cuse href=%22%23slobeInner%22 transform=%22rotate%280 50 50%29%22 /%3E %3Cuse href=%22%23slobeInner%22 transform=%22rotate%2860 50 50%29%22 /%3E %3Cuse href=%22%23slobeInner%22 transform=%22rotate%28120 50 50%29%22 /%3E %3Ccircle cx=%2250%22 cy=%2250%22 r=%223%22 fill=%22%2300b1b8%22 /%3E %3C/svg%3E",
  showThemeToggle: true,
} as OrbDocTutorialsAdvancedAiGenerationARDocHeaderConfig;

export interface OrbDocTutorialsAdvancedAiGenerationARDocHeaderLogicReturn {
  state: OrbDocTutorialsAdvancedAiGenerationARDocHeaderState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot, OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsAdvancedAiGenerationARDocViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap]: (payload?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocTutorialsAdvancedAiGenerationARDocHeaderConfig;
}

export function useOrbDocTutorialsAdvancedAiGenerationARDocHeaderLogic(config?: Partial<OrbDocTutorialsAdvancedAiGenerationARDocHeaderConfig>): OrbDocTutorialsAdvancedAiGenerationARDocHeaderLogicReturn {
  const mergedConfig: OrbDocTutorialsAdvancedAiGenerationARDocHeaderConfig = { ...DEFAULT_ORB_DOC_TUTORIALS_ADVANCED_AI_GENERATION_AR_DOC_HEADER_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-advanced-ai-generation-ar-doc-header', orbitalName: 'OrbDocTutorialsAdvancedAiGenerationAR', traitName: 'OrbDocTutorialsAdvancedAiGenerationARDocHeader' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsAdvancedAiGenerationARDocHeaderReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['OrbDocTutorialsAdvancedAiGenerationARDocView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['menuOpen'] = false;
        fields = { ...(fields ?? {}), 'menuOpen': _fields['menuOpen'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MENU_TOGGLE') {
      // idle → idle (on MENU_TOGGLE)
        _fields['menuOpen'] = !fields?.menuOpen;
        fields = { ...(fields ?? {}), 'menuOpen': _fields['menuOpen'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'NAV_LINK') {
      // idle → idle (on NAV_LINK)
        _fields['menuOpen'] = false;
        fields = { ...(fields ?? {}), 'menuOpen': _fields['menuOpen'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('NAV_CLICKED', _p, { trait: 'OrbDocTutorialsAdvancedAiGenerationARDocHeader' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsAdvancedAiGenerationAR', 'OrbDocTutorialsAdvancedAiGenerationARDocHeader', { event: 'NAV_CLICKED', payload: _p, source: { orbital: 'OrbDocTutorialsAdvancedAiGenerationAR', trait: 'OrbDocTutorialsAdvancedAiGenerationARDocHeader' } }); })({ href: payloadObj?.href, label: payloadObj?.label }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[]; clear: OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[], clear: [] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[] };
          case 'MENU_TOGGLE': return { write: ['main'] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[], clear: [] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[] };
          case 'NAV_LINK': return { write: ['main'] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[], clear: [] as OrbDocTutorialsAdvancedAiGenerationARDocHeaderSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsAdvancedAiGenerationARDocView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsAdvancedAiGenerationARDocHeaderEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsAdvancedAiGenerationARDocHeader': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap]: (payload?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MENU_TOGGLE': () => dispatch('MENU_TOGGLE'),
    'NAV_LINK': (payload?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap['NAV_LINK']) => dispatch('NAV_LINK', payload),
    'NAV_CLICKED': (payload?: OrbDocTutorialsAdvancedAiGenerationARDocHeaderEventPayloadMap['NAV_CLICKED']) => dispatch('NAV_CLICKED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsAdvancedAiGenerationAR.OrbDocTutorialsAdvancedAiGenerationARDocHeader', ['INIT', 'MENU_TOGGLE', 'NAV_LINK'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsAdvancedAiGenerationARDocHeader', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsAdvancedAiGenerationARDocHeader',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT', 'MENU_TOGGLE', 'NAV_LINK', 'NAV_CLICKED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.actionHref = mergedConfig.actionHref ?? (["/ar", "/downloads"].join(''));
  mergedConfig.actionLabel = mergedConfig.actionLabel ?? (__t("std-orb-chrome:nav.cta"));
  mergedConfig.currentPath = mergedConfig.currentPath ?? (["/ar", "/docs/tutorials/advanced/ai-generation"].join(''));
  mergedConfig.homeHref = mergedConfig.homeHref ?? ("/ar");
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
